import { DynamicResultType, DynamicsTest, ManualResultDetail } from './models';

import {
  AnonymousResultData,
  GetResultParams,
  GetResultsParams,
  Result,
  ResultFileData,
  SearchAnonymousReportRequest,
  SearchAnonymousResultRequest,
  SendRequest,
  SendResultParams,
} from '@/api/__generated__/webApi';
import { apiClient } from '@/client/ApiClient';
import { getRecaptchaToken } from '@/shared/hooks/useRecaptcha';

export * from './models';

/**
 * @description Getting account results
 *
 * @tags Results
 * @name GetResults
 * @summary Results
 * @request GET:/api/results
 * @docs https://patient-web.test.private.lifedl.net/swagger-ui/index.html#Results/getResults
 * @response `200` `(BaseResult)[]` OK
 * @response `401` `undefined` Unauthorized
 * @response `500` `BaseError` Internal Server Error
 */
export const getResults = async (query: GetResultsParams) => {
  const response = await apiClient.get<Result[]>('/api/results', {
    params: query,
  });
  return response.data;
};

/**
 * @description Getting account result
 *
 * @tags Results
 * @name GetResult
 * @summary Result
 * @request GET:/api/results/{id}
 * @docs https://patient-web.test.private.lifedl.net/swagger-ui/index.html#Results/getResult
 * @response `200` `BaseResultDetail` OK
 * @response `401` `undefined` Unauthorized
 * @response `403` `undefined` Forbidden
 * @response `404` `undefined` Not Found
 * @response `500` `BaseError` Internal Server Error
 */
export const getResultByid = async ({ id, ...query }: GetResultParams) => {
  const response = await apiClient.get<ManualResultDetail | Result>(
    `/api/results/${id}`,
    {
      params: query,
    }
  );
  return response.data;
};

/**
 * @description Send result
 *
 * @tags Results
 * @name SendResult
 * @summary Send result
 * @request POST:/api/results/{id}/fn/send
 * @docs https://patient-web.test.private.lifedl.net/swagger-ui/index.html#Results/sendResult
 * @response `200` `undefined` OK
 * @response `400` `ValidationError` Bad request
 * @response `401` `undefined` Unauthorized
 * @response `403` `undefined` Forbidden
 * @response `500` `BaseError` Internal Server Error
 */
export const sendResultsByEmail = async ({
  params: { id, ...query },
  data,
}: {
  params: SendResultParams;
  data: SendRequest;
}) => {
  void (await apiClient.post(`/api/results/${id}/fn/send`, data, {
    params: query,
  }));
};

export const getDynamics = async (): Promise<DynamicsTest[]> => {
  const response = await apiClient.post<DynamicsTest[]>(
    '/api/dynamics/search',
    {
      type: DynamicResultType.All,
    }
  );
  return response.data;
};

/**
 * @description Search anonymous result
 *
 * @tags ResultsAnonymous
 * @name SearchAnonymousResult
 * @summary Search anonymous result
 * @request POST:/api/anonymous/results/search
 * @docs https://patient-web.test.private.lifedl.net/swagger-ui/index.html#ResultsAnonymous/searchAnonymousResult
 * @response `200` `ResultFileData` OK
 * @response `400` `ValidationError` Bad Request
 * @response `403` `ThresholdError` Forbidden
 * @response `404` `undefined` Not Found
 * @response `429` `AttemptsError` To many requests
 * @response `500` `BaseError` Internal Server Error
 */
export const searchResults = async (data: SearchAnonymousResultRequest) => {
  const recaptcha = await getRecaptchaToken();
  return await apiClient.post<ResultFileData>('/api/anonymous/results/search', {
    ...data,
    recaptcha,
  });
};

/**
 * @description Search anonymous report
 *
 * @tags ResultsAnonymous
 * @name SearchAnonymousReport
 * @summary Search anonymous report
 * @request POST:/api/anonymous/results/search-report
 * @docs https://patient-web.test.private.lifedl.net/swagger-ui/index.html#ResultsAnonymous/searchAnonymousReport
 * @response `200` `AnonymousResultData` OK
 * @response `400` `ValidationError` Bad Request
 * @response `403` `ThresholdError` Forbidden
 * @response `404` `undefined` Not Found
 * @response `429` `AttemptsError` To many requests
 * @response `500` `BaseError` Internal Server Error
 */
export const searchAnonymousReport = async (
  data: SearchAnonymousReportRequest
) => {
  const recaptcha = await getRecaptchaToken();
  return await apiClient.post<AnonymousResultData>(
    '/api/anonymous/results/search-report',
    {
      ...data,
      recaptcha,
    }
  );
};

export * as resultsApi from './';
